import { c as _c } from "react/compiler-runtime";
import { breakpointQueries } from '@systemeio/util';
import debounce from 'lodash.debounce';
import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import useMediaQuery from 'shared/hooks/use-media-query';
interface NavbarLayoutDependencies {
  isLoading: boolean;
  isMenuReady: boolean;
  language?: string;
  menuItemCount: number;
  userLocale?: string;
}
interface NavbarLayoutElements {
  desktopActions: HTMLDivElement;
  desktopMenu: HTMLElement;
  nav: HTMLDivElement;
}
export function isDesktopLayoutOverflowing({
  desktopActions,
  desktopMenu,
  nav
}: NavbarLayoutElements) {
  const desktopMenuRect = desktopMenu.getBoundingClientRect();
  const desktopActionsRect = desktopActions.getBoundingClientRect();
  const desktopMenuContentRight = desktopMenuRect.left + desktopMenu.scrollWidth;
  return nav.scrollWidth > nav.offsetWidth || desktopMenu.scrollWidth > desktopMenu.offsetWidth || desktopMenuContentRight > desktopActionsRect.left;
}
export const isDesktopViewportWidth = () => window.matchMedia(breakpointQueries.isDesktop).matches;
export function useNavbarLayout(t0) {
  const $ = _c(26);
  const {
    isLoading,
    isMenuReady,
    language,
    menuItemCount,
    userLocale
  } = t0;
  const {
    isDesktop: matchesDesktopBreakpoint
  } = useMediaQuery();
  const [isDesktop, setIsDesktopState] = useState(matchesDesktopBreakpoint);
  const [isMounted, setIsMounted] = useState(false);
  const isDesktopRef = useRef(isDesktop);
  const navRef = useRef(null);
  const desktopMenuRef = useRef(null);
  const desktopActionsRef = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = value => {
      isDesktopRef.current = value;
      setIsDesktopState(value);
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const setIsDesktop = t1;
  let t2;
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      setIsMounted(true);
    };
    t3 = [];
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  useLayoutEffect(t2, t3);
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      const nav = navRef.current;
      const desktopMenu = desktopMenuRef.current;
      const desktopActions = desktopActionsRef.current;
      if (!nav || !desktopMenu || !desktopActions) {
        return;
      }
      if (isDesktopLayoutOverflowing({
        desktopActions,
        desktopMenu,
        nav
      })) {
        setIsDesktop(false);
      }
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const checkDesktopLayoutOverflow = t4;
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      const handleResize = debounce(() => {
        if (!isDesktopViewportWidth()) {
          setIsDesktop(false);
          return;
        }
        if (isDesktopRef.current) {
          checkDesktopLayoutOverflow();
          return;
        }
        setIsDesktop(true);
      }, 300, {
        leading: true,
        trailing: true
      });
      handleResize();
      window.addEventListener("resize", handleResize);
      return () => {
        window.removeEventListener("resize", handleResize);
        handleResize.cancel();
      };
    };
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== isLoading || $[6] !== isMenuReady || $[7] !== isMounted || $[8] !== language || $[9] !== menuItemCount || $[10] !== userLocale) {
    t6 = [checkDesktopLayoutOverflow, isLoading, isMenuReady, isMounted, language, menuItemCount, setIsDesktop, userLocale];
    $[5] = isLoading;
    $[6] = isMenuReady;
    $[7] = isMounted;
    $[8] = language;
    $[9] = menuItemCount;
    $[10] = userLocale;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  useLayoutEffect(t5, t6);
  let t7;
  if ($[12] !== isDesktop || $[13] !== isMounted) {
    t7 = () => {
      if (!isDesktop || !isMounted) {
        return;
      }
      if (!isDesktopViewportWidth()) {
        setIsDesktop(false);
        return;
      }
      checkDesktopLayoutOverflow();
      const elements = [navRef.current, desktopMenuRef.current, desktopActionsRef.current].filter(_temp);
      const resizeObserver = new ResizeObserver(checkDesktopLayoutOverflow);
      elements.forEach(element_0 => resizeObserver.observe(element_0));
      let cancelled = false;
      document.fonts?.ready.then(() => {
        if (!cancelled) {
          checkDesktopLayoutOverflow();
        }
      });
      return () => {
        cancelled = true;
        resizeObserver.disconnect();
      };
    };
    $[12] = isDesktop;
    $[13] = isMounted;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== isDesktop || $[16] !== isLoading || $[17] !== isMenuReady || $[18] !== isMounted || $[19] !== language || $[20] !== menuItemCount || $[21] !== userLocale) {
    t8 = [checkDesktopLayoutOverflow, isDesktop, isLoading, isMenuReady, isMounted, language, menuItemCount, setIsDesktop, userLocale];
    $[15] = isDesktop;
    $[16] = isLoading;
    $[17] = isMenuReady;
    $[18] = isMounted;
    $[19] = language;
    $[20] = menuItemCount;
    $[21] = userLocale;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  useLayoutEffect(t7, t8);
  let t9;
  if ($[23] !== isDesktop || $[24] !== isMounted) {
    t9 = {
      desktopActionsRef,
      desktopMenuRef,
      isDesktop,
      isMounted,
      navRef
    };
    $[23] = isDesktop;
    $[24] = isMounted;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  return t9;
}
function _temp(element) {
  return element !== null;
}